/**
 * Sync controller — the bridge between the WS protocol and the Player.
 *
 *   - room_state → load source, seek, send ready
 *   - schedule_event → setTimeout, fire play/pause/seek at the exact moment
 *   - drift loop → measure, then either slew rate, hard-seek, or ignore
 *   - buffering → notify server; on canplay → send ready
 *   - heartbeat every 2s
 *
 * The controller is framework-agnostic so it stays unit-testable; the React
 * layer just constructs one and subscribes to its state via the optional
 * `onState` callback.
 */
import {
  DRIFT_IGNORE_S,
  DRIFT_LOOP_MS,
  DRIFT_RATE_GAIN,
  DRIFT_RATE_MAX_DELTA,
  DRIFT_RATE_S,
  HEARTBEAT_INTERVAL_MS,
  type ServerMessage,
} from '@syncstream/protocol';
import { type PlayerLike } from './Player.ts';
import { WsClient } from '../ws/client.ts';

/**
 * Re-seek tolerance for scheduled play/pause events. If the player is already
 * within this many seconds of the target, skip the seek — it adds a buffering
 * stutter at playback start for no benefit when the player is already where it
 * needs to be.
 */
const SCHEDULED_EVENT_SEEK_TOLERANCE_S = 0.15;

export type DriftAction =
  | { type: 'none' }
  | { type: 'set_rate'; rate: number }
  | { type: 'hard_seek'; target: number };

/**
 * Pure drift-correction policy. Extracted from `driftTick` for testability.
 *
 *   |drift| < IGNORE       → no action (reset rate to 1 if previously slewed)
 *   |drift| < RATE         → slew rate proportionally, clamped
 *   |drift| < HARD_SEEK    → hard seek to expected
 *   |drift| >= HARD_SEEK   → hard seek (server will also force_resync via heartbeat)
 */
export function computeDriftAction(
  drift: number,
  expected: number,
  currentRate: number,
  anchorRate: number,
): DriftAction {
  const abs = Math.abs(drift);
  if (abs < DRIFT_IGNORE_S) {
    // In deadband: restore native rate if we were slewing.
    if (Math.abs(currentRate - anchorRate) > 0.001) {
      return { type: 'set_rate', rate: anchorRate };
    }
    return { type: 'none' };
  }
  if (abs < DRIFT_RATE_S) {
    const raw = anchorRate - drift * DRIFT_RATE_GAIN;
    const lo = anchorRate - DRIFT_RATE_MAX_DELTA;
    const hi = anchorRate + DRIFT_RATE_MAX_DELTA;
    const rate = raw < lo ? lo : raw > hi ? hi : raw;
    if (Math.abs(currentRate - rate) < 0.001) return { type: 'none' };
    return { type: 'set_rate', rate };
  }
  return { type: 'hard_seek', target: expected };
}

export interface SyncStateSnapshot {
  state: 'playing' | 'paused' | 'connecting';
  position: number;
  members: number;
  waiting: boolean;
  driftS: number;
  clockOffsetMs: number;
  videoTitle: string | null;
  error: string | null;
}

export interface SyncControllerOptions {
  wsUrl: string;
  videoTitle?: string | null;
  onState?: (s: SyncStateSnapshot) => void;
  /** Test seam: passed through to the underlying WsClient. */
  webSocketFactory?: (url: string) => WebSocket;
}

export class SyncController {
  private readonly ws: WsClient;
  private readonly snapshot: SyncStateSnapshot;
  private driftTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** Authoritative state derived from `room_state` + `schedule_event`. */
  private anchor: {
    state: 'playing' | 'paused';
    positionAtAnchorS: number;
    anchorServerTimeMs: number;
    playbackRate: number;
  } | null = null;
  private joined = false;

  constructor(
    private readonly player: PlayerLike,
    private readonly opts: SyncControllerOptions,
  ) {
    this.snapshot = {
      state: 'connecting',
      position: 0,
      members: 0,
      waiting: true,
      driftS: 0,
      clockOffsetMs: 0,
      videoTitle: opts.videoTitle ?? null,
      error: null,
    };

    this.ws = new WsClient({
      url: opts.wsUrl,
      webSocketFactory: opts.webSocketFactory,
      onMessage: (m) => this.onServerMessage(m),
      onOpen: () => {
        this.snapshot.error = null;
        this.publish();
      },
      onClose: () => {
        this.snapshot.state = 'connecting';
        this.publish();
      },
      onClockUpdate: (o) => {
        this.snapshot.clockOffsetMs = o;
        this.publish();
      },
    });

    this.bindPlayer();
  }

  start(): void {
    this.ws.connect();
    this.driftTimer = setInterval(() => this.driftTick(), DRIFT_LOOP_MS);
    this.heartbeatTimer = setInterval(() => this.heartbeatTick(), HEARTBEAT_INTERVAL_MS);
  }

  stop(): void {
    this.ws.close();
    if (this.driftTimer != null) clearInterval(this.driftTimer);
    if (this.heartbeatTimer != null) clearInterval(this.heartbeatTimer);
  }

  // User actions ───────────────────────────────────────────────────────────
  play(): void {
    if (!this.anchor) return;
    this.ws.send({ type: 'play', at_position: this.player.getCurrentTime() });
  }
  pause(): void {
    if (!this.anchor) return;
    this.ws.send({ type: 'pause', at_position: this.player.getCurrentTime() });
  }
  seek(target: number): void {
    if (!this.anchor) return;
    this.ws.send({ type: 'seek', target_position: target });
  }

  // Player events ───────────────────────────────────────────────────────────
  private bindPlayer(): void {
    this.player.on('stalled', () => {
      if (!this.anchor || this.anchor.state !== 'playing') return;
      this.ws.send({ type: 'buffering', at_position: this.player.getCurrentTime() });
      this.snapshot.waiting = true;
      this.publish();
    });
    this.player.on('canplay', () => {
      if (!this.joined) {
        this.joined = true;
        this.ws.send({ type: 'ready', reason: 'join' });
        // The server only broadcasts `wait_for_ready` for buffering cycles
        // (a connection has to be in `waitingFor` for the broadcast to fire),
        // not for joins. Without clearing locally, the "syncing…" overlay set
        // by applyRoomState would stick forever — and a follow-up canplay
        // (e.g. after a scheduled pause's seekIfFar) would see `waiting` still
        // true and send `ready{buffer}`, which the server interprets as
        // buffering recovery and resumes the room, undoing the pause.
        if (this.snapshot.waiting) {
          this.snapshot.waiting = false;
          this.publish();
        }
      } else if (this.snapshot.waiting) {
        this.ws.send({ type: 'ready', reason: 'buffer' });
      }
    });
    this.player.on('error', (e) => {
      this.snapshot.error = (e as { message?: string }).message ?? 'player error';
      this.publish();
    });
  }

  // Server messages ─────────────────────────────────────────────────────────
  private onServerMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'room_state':
        this.applyRoomState(msg);
        break;
      case 'schedule_event':
        this.applyScheduledEvent(msg);
        break;
      case 'wait_for_ready':
        this.snapshot.waiting = msg.waiting_count > 0;
        this.publish();
        break;
      case 'member_count':
        this.snapshot.members = msg.count;
        this.publish();
        break;
      case 'force_resync':
        this.player.seekTo(msg.target_position);
        this.publish();
        break;
      case 'error':
        this.snapshot.error = `${msg.code}: ${msg.message}`;
        this.publish();
        break;
      case 'clock_sync_response':
        // handled inside WsClient
        break;
    }
  }

  private applyRoomState(msg: Extract<ServerMessage, { type: 'room_state' }>): void {
    this.anchor = {
      state: msg.state,
      positionAtAnchorS: msg.position,
      anchorServerTimeMs: msg.anchor_server_time,
      playbackRate: msg.playback_rate,
    };
    this.snapshot.members = msg.members;
    this.snapshot.state = msg.state;
    this.snapshot.videoTitle = this.opts.videoTitle ?? this.snapshot.videoTitle;
    this.snapshot.position = msg.position;

    this.player.loadSource(msg.video_url);
    this.player.setPlaybackRate(msg.playback_rate);

    // Seek to current position (compute against server-now for ongoing playback)
    const target = this.serverExpectedPosition();
    this.player.seekTo(target);
    if (msg.state === 'paused') this.player.pause();
    // canplay listener will fire `ready{join}` for us.

    this.snapshot.waiting = true; // wait until canplay
    this.joined = false;
    this.publish();
  }

  private applyScheduledEvent(msg: Extract<ServerMessage, { type: 'schedule_event' }>): void {
    const localExecuteMs = msg.execute_at_server_time - this.ws.getClockOffset();
    const delay = Math.max(0, localExecuteMs - Date.now());
    setTimeout(() => {
      switch (msg.action) {
        case 'play':
          if (this.anchor) {
            this.anchor.state = 'playing';
            this.anchor.positionAtAnchorS = msg.target_position;
            this.anchor.anchorServerTimeMs = msg.execute_at_server_time;
          }
          this.seekIfFar(msg.target_position);
          void this.player.play();
          this.snapshot.state = 'playing';
          break;
        case 'pause':
          if (this.anchor) {
            this.anchor.state = 'paused';
            this.anchor.positionAtAnchorS = msg.target_position;
            this.anchor.anchorServerTimeMs = msg.execute_at_server_time;
          }
          this.player.pause();
          this.seekIfFar(msg.target_position);
          this.snapshot.state = 'paused';
          break;
        case 'seek':
          if (this.anchor) {
            this.anchor.positionAtAnchorS = msg.target_position;
            this.anchor.anchorServerTimeMs = msg.execute_at_server_time;
          }
          // User-initiated seek: always honor regardless of proximity.
          this.player.seekTo(msg.target_position);
          break;
      }
      this.publish();
    }, delay);
  }

  /** Seek only if the player is more than the tolerance away from the target. */
  private seekIfFar(target: number): void {
    if (Math.abs(this.player.getCurrentTime() - target) > SCHEDULED_EVENT_SEEK_TOLERANCE_S) {
      this.player.seekTo(target);
    }
  }

  // Drift loop ─────────────────────────────────────────────────────────────
  private driftTick(): void {
    if (!this.anchor) return;
    const expected = this.serverExpectedPosition();
    const actual = this.player.getCurrentTime();
    const drift = actual - expected;
    this.snapshot.driftS = drift;
    this.snapshot.position = actual;
    this.publish();

    if (this.anchor.state !== 'playing') return;

    const action = computeDriftAction(
      drift,
      expected,
      this.player.getPlaybackRate(),
      this.anchor.playbackRate,
    );
    switch (action.type) {
      case 'none':
        return;
      case 'set_rate':
        this.player.setPlaybackRate(action.rate);
        return;
      case 'hard_seek':
        this.player.seekTo(action.target);
        // Restore native rate after seek; drift was outside the slew region.
        if (Math.abs(this.player.getPlaybackRate() - this.anchor.playbackRate) > 0.001) {
          this.player.setPlaybackRate(this.anchor.playbackRate);
        }
        return;
    }
  }

  private heartbeatTick(): void {
    this.ws.send({
      type: 'heartbeat',
      client_time: Date.now(),
      client_position: this.player.getCurrentTime(),
    });
  }

  private serverExpectedPosition(): number {
    if (!this.anchor) return 0;
    if (this.anchor.state === 'paused') return this.anchor.positionAtAnchorS;
    const nowSrv = this.ws.serverNow();
    const elapsedS = (nowSrv - this.anchor.anchorServerTimeMs) / 1000;
    return this.anchor.positionAtAnchorS + elapsedS * this.anchor.playbackRate;
  }

  private publish(): void {
    this.opts.onState?.({ ...this.snapshot });
  }
}
